import axios from 'axios';

/**
 * DP98 Part 1 — ONE ANSWER PER SESSION, AND NOBODY DRAWS BEFORE IT ARRIVES.
 *
 * Chad, on the deployed app: "When I select My Desk for a brief moment the
 * masthead shows the Planes and then switches to the image I loaded. All three
 * do this." Each band fetched the company settings itself, on mount, and drew
 * 'planes' — the default — while the request was in flight. Three gateways, three
 * fetches, three flashes.
 *
 * This holds the answer for the session, so moving between gateways does not ask
 * again, and it lets a band wait rather than guess.
 *
 * A cached answer still has to change when an admin changes it. Two ways in:
 *   - invalidate(next) — the Branding tab calls this after a successful save, so
 *     the admin's own tabs are right immediately, with no round trip;
 *   - the same call is what a branding broadcast should land on. DP92 Part D
 *     built that broadcast and it cannot connect in preview (the socket paths sit
 *     outside /api and are fenced), so this pass wires the save path and leaves
 *     the socket alone.
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let pending = null;
let value = null;
let generation = 0;
const listeners = new Set();

const ask = () => {
  const mine = generation;
  pending = axios.get(`${API}/company-settings`)
    .then((res) => {
      // A save landed while this was in flight: its answer is older than what
      // the session now holds, so it is dropped rather than written back.
      if (mine !== generation) return value || {};
      value = res.data || {};
      pending = null;
      return value;
    })
    .catch((err) => {
      if (mine === generation) pending = null;
      throw err;
    });
  return pending;
};

/**
 * DP99 pass 1 — every caller reads this, not the endpoint.
 *
 * `force` is for the two settings EDITORS, which must open on the stored record
 * rather than on a copy held since login. A request already in flight IS a
 * fresh read, so forcing joins it instead of starting a second one; that is what
 * keeps a document load to one request even when an editor is on it.
 */
export const getCompanySettings = ({ force = false } = {}) => {
  if (force) {
    if (pending) return pending;
    value = null;
  }
  if (value) return Promise.resolve(value);
  return pending || ask();
};

/** The cached answer, or null if the session has not been told yet. */
export const peekCompanySettings = () => value;

/**
 * After a save, or a broadcast: replace the answer and tell every mounted band.
 * Called with null it clears the answer and refetches, so subscribers see null
 * for one render before the new answer arrives — a subscriber must tolerate it.
 */
export const invalidateCompanySettings = (next) => {
  generation += 1;
  pending = null;
  value = next && typeof next === 'object' ? next : null;
  listeners.forEach((fn) => { try { fn(value); } catch { /* a listener unmounting */ } });
  if (!value) getCompanySettings().then((v) => listeners.forEach((fn) => fn(v))).catch(() => {});
};

export const subscribeCompanySettings = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

export default getCompanySettings;
